export enum ProctoringEventType {
    Blur = 'blur',
    TabHidden = 'tab hidden',
    TabVisible = 'tab visible',
    WindowBlur = 'window blur',
    WindowFocus = 'window focus',
    ScreenShareStopped = 'screen share stopped',
    NoFace = 'no face',
    MultipleFaces = 'multiple faces',
    /** Internal end marker for face-warning episodes; not shown in report UI. */
    FaceOk = 'face ok',
    CameraOff = 'camera off',
    CameraError = 'camera error',
    FullscreenEnter = 'fullscreen enter',
    FullscreenExit = 'fullscreen exit',
    DisconnectUserDisconnected = 'disconnect:user_disconnected',
    DisconnectCameraOff = 'disconnect:camera_off',
    DisconnectFaceUndetected = 'disconnect:face_undetected',
    DisconnectMultipleFaces = 'disconnect:multiple_faces',
    DisconnectIdleTimeout = 'disconnect:idle_timeout',
    DisconnectUserRequested = 'disconnect:user_requested',
    DisconnectFullscreenExited = 'disconnect:fullscreen_exited',
}

export type ProctoringLeaveReason =
    | 'user_disconnected'
    | 'camera_off'
    | 'face_undetected'
    | 'multiple_faces'
    | 'idle_timeout'
    | 'user_requested'
    | 'fullscreen_exited'

const DisconnectReasonMap: Record<ProctoringLeaveReason, ProctoringEventType> = {
    user_disconnected: ProctoringEventType.DisconnectUserDisconnected,
    camera_off: ProctoringEventType.DisconnectCameraOff,
    face_undetected: ProctoringEventType.DisconnectFaceUndetected,
    multiple_faces: ProctoringEventType.DisconnectMultipleFaces,
    idle_timeout: ProctoringEventType.DisconnectIdleTimeout,
    user_requested: ProctoringEventType.DisconnectUserRequested,
    fullscreen_exited: ProctoringEventType.DisconnectFullscreenExited,
}

export function proctoringDisconnectEvent(reason: ProctoringLeaveReason): ProctoringEventType {
    return DisconnectReasonMap[reason]
}

export interface ProctoringEvent {
    type: ProctoringEventType
    timestamp: string
}

export function getProctoringEventLabel(type: ProctoringEventType | string): string {
    switch (type) {
        case ProctoringEventType.Blur:
            return 'Clicked away from interview'
        case ProctoringEventType.TabHidden:
            return 'Tab hidden'
        case ProctoringEventType.TabVisible:
            return 'Tab visible'
        case ProctoringEventType.WindowBlur:
            return 'Window blurred'
        case ProctoringEventType.WindowFocus:
            return 'Window focused'
        case ProctoringEventType.ScreenShareStopped:
            return 'Screen share stopped'
        case ProctoringEventType.NoFace:
            return 'No face detected'
        case ProctoringEventType.MultipleFaces:
            return 'Multiple faces detected'
        case ProctoringEventType.FaceOk:
            return 'Face restored'
        case ProctoringEventType.CameraOff:
            return 'Camera turned off'
        case ProctoringEventType.FullscreenEnter:
            return 'Fullscreen entered'
        case ProctoringEventType.FullscreenExit:
            return 'Fullscreen exited'
        case ProctoringEventType.DisconnectUserDisconnected:
            return 'User disconnected'
        case ProctoringEventType.DisconnectCameraOff:
            return 'Camera off (interview ended)'
        case ProctoringEventType.DisconnectFaceUndetected:
            return 'Face undetected (interview ended)'
        case ProctoringEventType.DisconnectMultipleFaces:
            return 'Multiple faces (interview ended)'
        case ProctoringEventType.DisconnectIdleTimeout:
            return 'Idle timeout (interview ended)'
        case ProctoringEventType.DisconnectUserRequested:
            return 'User requested end'
        case ProctoringEventType.DisconnectFullscreenExited:
            return 'Fullscreen exited (interview ended)'
        default:
            return type
    }
}
